import { Fragment, useContext } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { TextReviewedCareElement } from "@/Flowsheets/Models/CareElements/TextReviewedCareElement";
import CareElementTextAreaField from "./CareComponentParts/CareElementTextAreaField";
import { CareElementDateReviewed } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewed";
import { CareElementWithFrequencyLabel } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementWithFrequencyLabel";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./SelfManagementPlanCareComponent.module.scss";

interface SelfManagementPlanCareComponent extends CareComponent {
    CareElements: TextReviewedCareElement[];
}

function SelfManagementPlanInput({
    keyName,
    state,
    isEditable,
    label,
    onChange
}: Readonly<{
    keyName: string,
    state: TextReviewedCareElement | null,
    isEditable: boolean,
    label: string,
    onChange: (newState: TextReviewedCareElement) => void,
    onCareComponentValidityChange?: (id: string, isValid: boolean) => void,
    getText: (key: string) => string
}>): JSX.Element {
    const { encounterDate, encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    function handleValueChange(value: string | null, date: DateTimeWithPrecision | null): void {
        const newElement: TextReviewedCareElement = {
            ...state,
            Value: value,
            DateReviewedWithPrecision: date
        };

        onChange(newElement);
    }

    return (
        <>
            <div className={styles.spanRight}>
                <CareElementWithFrequencyLabel
                    label={label}
                    keyName={keyName}
                    state={state}
                    encounterDate={encounterDate}
                />
            </div>
            <div className="margin-md-bottom">
                <CareElementTextAreaField
                    id={`selfManagementPlan-${state.Name}`}
                    value={state.Value}
                    isEditable={isEditable}
                    onTextChange={textVal => {
                        const isValid = !IsNullOrWhiteSpace(textVal);

                        const shouldAutoFillDate = isValid && !state.DateReviewedWithPrecision?.value;
                        let dateToUse = null;
                        if (isValid) {
                            dateToUse = shouldAutoFillDate
                                ? encounterDateTimeWithPrecision
                                : state.DateReviewedWithPrecision;
                        }

                        handleValueChange(textVal, dateToUse);
                    }}
                    dataCy={`SelfManagementPlanNotes-${state.Name}`}
                />
            </div>

            <CareElementDateReviewed
                id={`${FlowsheetConstants.SelfManagementPlan.CareComponentName}-${state.Name}-dateReviewed`}
                isEditable={isEditable}
                dataCy={`${FlowsheetConstants.SelfManagementPlan.CareComponentName}-${state.Name}-dateReviewed`}
                encounterDate={encounterDate}
                state={state}
                handleChange={date => handleValueChange(state.Value, date)}
            />

        </>
    );
}

const SelfManagementPlanCareComponent = React.memo(
    ({ isEditable, state, mutateState, onCareComponentValidityChange }: ICareComponentProps<SelfManagementPlanCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        if (state?.CareElements == null
            || state.CareElements.length === 0
            || state.CareElements[0].CareElementType !== CareElementType.TextReviewed) {
            return null;
        }

        function getLabel(name: string): string {
            return getText(`Flowsheets_SelfManagementPlanCareComponentLabel_${name}`);
        }

        function handleValueChange(currentElement: TextReviewedCareElement, newValue: TextReviewedCareElement): void {
            mutateState(s => {
                const index = s.CareElements.findIndex(p => p.Name === currentElement.Name);
                if (index !== -1) {
                    const updatedElements = [...s.CareElements];
                    updatedElements[index] = newValue;
                    s.CareElements = updatedElements;
                }
            });
        }

        return (
            <div>
                <ReviewedCareElementContainer>
                    {state.CareElements.map(element => (
                        <Fragment key={`selfManagementPlan-${state.Id}-${element.Name}`}>
                            <SelfManagementPlanInput
                                label={`${getLabel(element.Name)}`}
                                state={element}
                                onChange={newElement => handleValueChange(element, newElement)}
                                keyName={`selfManagementPlan-${state.Id}-${element.Name}`}
                                isEditable={isEditable}
                                onCareComponentValidityChange={onCareComponentValidityChange}
                                getText={getText}
                            />
                        </Fragment>
                    ))}
                </ReviewedCareElementContainer>
            </div>
        );
    }
);

export { SelfManagementPlanCareComponent };
